/**
 * Ticket System API Service
 * Handles all API calls for the ticket management system
 */

import axios from 'axios'
import apiClient from '../client'
import {
  TicketSummaryDto,
  TicketDetailsDto,
  TicketCommentDto,
  TicketAttachmentDto,
  TicketStatisticsDto,
  CreateTicketRequest,
  AddCommentRequest,
  ResolveTicketRequest,
  CloseTicketRequest,
  UpdatePriorityRequest,
  EscalateTicketRequest,
  AssignTicketRequest,
  TicketStatus,
  TicketPriority,
  TicketType,
  RouteReportEvidence,
  TicketListFilter,
  AdventureValidationDto,
  ApproveOwnershipClaimRequest,
  RejectOwnershipClaimRequest,
  OwnershipClaimDecisionDto,
} from '../types/tickets'

const BASE_PATH = '/api/Tickets'

/**
 * Query params for a list filter. `validation=true` wins over `types` (the API treats it as the
 * whole 30-39 band); nothing is sent when no filter is set, so existing calls are unchanged.
 */
function filterParams(filter?: TicketListFilter): Record<string, string> {
  if (!filter) return {}
  if (filter.validation) return { validation: 'true' }
  if (filter.types && filter.types.length > 0) return { types: filter.types.join(',') }
  return {}
}

// User Endpoints

/**
 * Upload one photo for a ticket or comment about to be sent. The server strips its EXIF (location
 * included), keeps it privately and returns its id plus short-lived signed preview links; name the id
 * in `attachmentIds` on the create or comment. 503 while ticket photos are not configured.
 */
export async function uploadTicketAttachment(file: Blob, fileName = 'photo.jpg'): Promise<TicketAttachmentDto> {
  const form = new FormData()
  form.append('file', file, fileName)
  const response = await apiClient.post<TicketAttachmentDto>(`${BASE_PATH}/attachments`, form, {
    headers: { 'Content-Type': undefined },
  })
  return response.data
}

/**
 * Create a new ticket
 */
export async function createTicket(request: CreateTicketRequest): Promise<TicketDetailsDto> {
  const response = await apiClient.post<TicketDetailsDto>(BASE_PATH, request)
  return response.data
}

/**
 * Get current user's tickets with pagination
 */
export async function getMyTickets(page = 1, pageSize = 20): Promise<TicketSummaryDto[]> {
  const response = await apiClient.get<TicketSummaryDto[]>(`${BASE_PATH}/my-tickets`, {
    params: { page, pageSize },
  })
  return response.data
}

/**
 * Get detailed information about a specific ticket
 */
export async function getTicketDetails(ticketId: string): Promise<TicketDetailsDto> {
  const response = await apiClient.get<TicketDetailsDto>(`${BASE_PATH}/${ticketId}`)
  return response.data
}

/**
 * Add a comment to a ticket
 */
export async function addComment(ticketId: string, request: AddCommentRequest): Promise<TicketCommentDto> {
  const response = await apiClient.post<TicketCommentDto>(`${BASE_PATH}/${ticketId}/comments`, request)
  return response.data
}

/**
 * Get all comments for a ticket
 */
export async function getTicketComments(ticketId: string): Promise<TicketCommentDto[]> {
  const response = await apiClient.get<TicketCommentDto[]>(`${BASE_PATH}/${ticketId}/comments`)
  return response.data
}

/**
 * Close a ticket: its creator, whoever claimed/was assigned it, staff, group moderators, validation managers
 */
export async function closeTicket(ticketId: string, request?: CloseTicketRequest): Promise<TicketDetailsDto> {
  const response = await apiClient.post<TicketDetailsDto>(`${BASE_PATH}/${ticketId}/close`, request)
  return response.data
}

/**
 * Close a ticket as invalid, spam or a duplicate. The reason is required and reaches the reporter.
 * Requires: a moderator who may act on the ticket (the reporter can close, not reject).
 */
export async function rejectTicket(ticketId: string, reason: string): Promise<TicketDetailsDto> {
  return closeTicket(ticketId, { reason, rejected: true })
}

// Moderator Endpoints

/** What `GET api/tickets/query` sorts by. Each is one fixed direction on the server. */
export type TicketQuerySort = 'priority' | 'activity' | 'newest' | 'oldest' | 'score' | 'escalation'

/**
 * Filters for `GET api/tickets/query`, the one paged ticket list with a real total. The old pool,
 * claimed and assigned endpoints return a bare array with no count, which is why the moderator
 * list could only ever show its first page.
 */
export interface TicketQueryParams {
  /** `queue` = what the caller works as a moderator (the pool's rule); `visible` = everything they may open. */
  scope?: 'queue' | 'visible'
  /** `open` (server default), `all`, `closed`, or a comma list of TicketStatus numbers. */
  status?: string
  filter?: TicketListFilter
  /** `me`, `unclaimed`, `claimed`, `any` or a user id. */
  claimedBy?: string
  /** A user id or `me`. */
  assignedTo?: string
  /** Escalation levels to keep (0 Owner, 1 Mods, 2 System Moderators). */
  escalation?: number[]
  q?: string
  sort?: TicketQuerySort
  skip?: number
  /** At most 100 (server cap). */
  take?: number
}

export interface TicketQueryPage {
  items: TicketSummaryDto[]
  totalCount: number
  skip: number
  take: number
  hasMore: boolean
}

export async function queryTickets(params: TicketQueryParams, signal?: AbortSignal): Promise<TicketQueryPage> {
  const { filter, escalation, ...rest } = params
  const query: Record<string, string | number> = { ...filterParams(filter) }
  for (const [key, value] of Object.entries(rest)) {
    if (value !== undefined && value !== null && value !== '') query[key] = value as string | number
  }
  if (escalation && escalation.length > 0) query.escalation = escalation.join(',')
  const response = await apiClient.get<TicketQueryPage>(`${BASE_PATH}/query`, { params: query, signal })
  return response.data
}

/**
 * Get available tickets pool (pending tickets sorted by priority)
 * Requires: Moderator role
 */
export async function getTicketPool(
  page = 1,
  pageSize = 20,
  filter?: TicketListFilter
): Promise<TicketSummaryDto[]> {
  const response = await apiClient.get<TicketSummaryDto[]>(`${BASE_PATH}/pool`, {
    params: { page, pageSize, ...filterParams(filter) },
  })
  return response.data
}

/**
 * Get tickets claimed by current moderator
 * Requires: Moderator role
 */
export async function getMyClaimedTickets(
  page = 1,
  pageSize = 20,
  filter?: TicketListFilter
): Promise<TicketSummaryDto[]> {
  const response = await apiClient.get<TicketSummaryDto[]>(`${BASE_PATH}/claimed`, {
    params: { page, pageSize, ...filterParams(filter) },
  })
  return response.data
}

/**
 * Get tickets assigned to current moderator
 * Requires: Moderator role
 */
export async function getMyAssignedTickets(
  page = 1,
  pageSize = 20,
  filter?: TicketListFilter
): Promise<TicketSummaryDto[]> {
  const response = await apiClient.get<TicketSummaryDto[]>(`${BASE_PATH}/assigned`, {
    params: { page, pageSize, ...filterParams(filter) },
  })
  return response.data
}

/**
 * Claim a ticket from the pool
 * Requires: Moderator role
 */
export async function claimTicket(ticketId: string): Promise<TicketDetailsDto> {
  const response = await apiClient.post<TicketDetailsDto>(`${BASE_PATH}/${ticketId}/claim`)
  return response.data
}

/**
 * Unclaim a previously claimed ticket
 * Requires: Moderator role
 */
export async function unclaimTicket(ticketId: string): Promise<TicketDetailsDto> {
  const response = await apiClient.post<TicketDetailsDto>(`${BASE_PATH}/${ticketId}/unclaim`)
  return response.data
}

/**
 * Start progress on a ticket
 * Requires: Moderator role
 */
export async function startProgress(ticketId: string): Promise<TicketDetailsDto> {
  const response = await apiClient.post<TicketDetailsDto>(`${BASE_PATH}/${ticketId}/start-progress`)
  return response.data
}

/**
 * Mark ticket as awaiting user response
 * Requires: Moderator role
 */
export async function markAwaitingUser(ticketId: string): Promise<TicketDetailsDto> {
  const response = await apiClient.post<TicketDetailsDto>(`${BASE_PATH}/${ticketId}/await-user`)
  return response.data
}

/**
 * Resolve a ticket
 * Requires: Moderator role
 */
export async function resolveTicket(ticketId: string, request: ResolveTicketRequest): Promise<TicketDetailsDto> {
  const response = await apiClient.post<TicketDetailsDto>(`${BASE_PATH}/${ticketId}/resolve`, request)
  return response.data
}

/**
 * Reopen a closed or resolved ticket
 * Requires: Moderator role or ticket creator
 */
export async function reopenTicket(ticketId: string): Promise<TicketDetailsDto> {
  const response = await apiClient.post<TicketDetailsDto>(`${BASE_PATH}/${ticketId}/reopen`)
  return response.data
}

// Ownership claims (site Moderators/Admins only)

/**
 * Approve an ownership claim: the adventure moves to the claimant (or the group they named), the
 * claimant becomes its creator and, unless keepMaintainers, every maintainer loses access.
 * 409 when the claim was already decided.
 */
export async function approveOwnershipClaim(
  ticketId: string,
  request: ApproveOwnershipClaimRequest = {}
): Promise<OwnershipClaimDecisionDto> {
  const response = await apiClient.post<OwnershipClaimDecisionDto>(`${BASE_PATH}/${ticketId}/ownership-claim/approve`, request)
  return response.data
}

/** Reject an ownership claim. The reason is required and reaches the claimant. */
export async function rejectOwnershipClaim(
  ticketId: string,
  reason: string,
  revealClaimant?: boolean | null
): Promise<OwnershipClaimDecisionDto> {
  const body: RejectOwnershipClaimRequest = { reason }
  if (revealClaimant !== undefined && revealClaimant !== null) body.revealClaimant = revealClaimant
  const response = await apiClient.post<OwnershipClaimDecisionDto>(`${BASE_PATH}/${ticketId}/ownership-claim/reject`, body)
  return response.data
}

/**
 * Whether the adventure's current owner is told who claims it. Site Moderators/Admins can switch it
 * at any time; the change is logged in the ticket history.
 */
export async function setOwnershipClaimReveal(ticketId: string, reveal: boolean): Promise<TicketDetailsDto> {
  const response = await apiClient.put<TicketDetailsDto>(`${BASE_PATH}/${ticketId}/ownership-claim/reveal`, { reveal })
  return response.data
}

// Admin Endpoints

/**
 * Update ticket priority
 * Requires: Admin role
 */
export async function updatePriority(ticketId: string, request: UpdatePriorityRequest): Promise<TicketDetailsDto> {
  const response = await apiClient.put<TicketDetailsDto>(`${BASE_PATH}/${ticketId}/priority`, request)
  return response.data
}

/**
 * Escalate a ticket
 * Requires: Admin role
 */
export async function escalateTicket(ticketId: string, request: EscalateTicketRequest): Promise<TicketDetailsDto> {
  const response = await apiClient.post<TicketDetailsDto>(`${BASE_PATH}/${ticketId}/escalate`, request)
  return response.data
}

/**
 * Assign ticket to a specific moderator
 * Requires: Admin role
 */
export async function assignTicket(ticketId: string, request: AssignTicketRequest): Promise<TicketDetailsDto> {
  const response = await apiClient.post<TicketDetailsDto>(`${BASE_PATH}/${ticketId}/assign`, request)
  return response.data
}

// Filtering & Search Endpoints

/**
 * Get tickets by status
 * Requires: Moderator role
 */
export async function getTicketsByStatus(
  status: TicketStatus,
  page = 1,
  pageSize = 20,
  filter?: TicketListFilter
): Promise<TicketSummaryDto[]> {
  const response = await apiClient.get<TicketSummaryDto[]>(`${BASE_PATH}/status/${TicketStatus[status]}`, {
    params: { page, pageSize, ...filterParams(filter) },
  })
  return response.data
}

/**
 * Get tickets by priority
 * Requires: Moderator role
 */
export async function getTicketsByPriority(
  priority: TicketPriority,
  page = 1,
  pageSize = 20
): Promise<TicketSummaryDto[]> {
  const response = await apiClient.get<TicketSummaryDto[]>(`${BASE_PATH}/priority/${TicketPriority[priority]}`, {
    params: { page, pageSize },
  })
  return response.data
}

/**
 * Get tickets by type
 * Requires: Moderator role
 */
export async function getTicketsByType(type: TicketType, page = 1, pageSize = 20): Promise<TicketSummaryDto[]> {
  const response = await apiClient.get<TicketSummaryDto[]>(`${BASE_PATH}/type/${TicketType[type]}`, {
    params: { page, pageSize },
  })
  return response.data
}

/**
 * Get escalated tickets
 * Requires: Admin role
 */
export async function getEscalatedTickets(page = 1, pageSize = 20): Promise<TicketSummaryDto[]> {
  const response = await apiClient.get<TicketSummaryDto[]>(`${BASE_PATH}/escalated`, {
    params: { page, pageSize },
  })
  return response.data
}

/**
 * Search tickets by keyword
 * Requires: Moderator role
 */
export async function searchTickets(searchTerm: string, page = 1, pageSize = 20): Promise<TicketSummaryDto[]> {
  const response = await apiClient.get<TicketSummaryDto[]>(`${BASE_PATH}/search`, {
    // The controller binds `q` (TicketsController.SearchTickets); `searchTerm` always came back 400.
    params: { q: searchTerm, page, pageSize },
  })
  return response.data
}

/**
 * Get ticket statistics
 * Requires: Admin role
 */
export async function getTicketStatistics(): Promise<TicketStatisticsDto> {
  const response = await apiClient.get<TicketStatisticsDto>(`${BASE_PATH}/statistics`)
  return response.data
}

/**
 * Check if current user has pending moderation tickets
 * Returns true if the user has any pending tickets they can moderate
 * Checks both system-level tickets (for Moderators/Admins) and group-level tickets
 */
export async function hasPendingModerationTickets(): Promise<boolean> {
  const response = await apiClient.get<boolean>(`${BASE_PATH}/has-pending`)
  return response.data
}

/**
 * Route evidence for a report: the published route and the reporter's walked track.
 * Requires: Moderator or Admin. 404 when the ticket is not a route report, or when its
 * evidence has already been pruned.
 */
export async function getRouteEvidence(ticketId: string): Promise<RouteReportEvidence | null> {
  try {
    const response = await apiClient.get<RouteReportEvidence>(`${BASE_PATH}/${ticketId}/route-evidence`)
    return response.data
  } catch (err) {
    // A ticket with no evidence is the ordinary case, not a failure — most tickets are not
    // route reports. The caller draws nothing rather than showing an error.
    if (axios.isAxiosError(err) && err.response?.status === 404) return null
    throw err
  }
}

// Validation items on an adventure (Backlog 057). User API, not the Admin API.

/**
 * Validation items on one adventure: open first, then up to 20 recently resolved.
 * Managers and Moderator/Admin only. Returns null on 404, which is what everyone else gets
 * (the API never says 403 here, so as not to disclose that items exist).
 */
export async function getAdventureValidation(adventureId: string): Promise<AdventureValidationDto | null> {
  try {
    const response = await apiClient.get<AdventureValidationDto>(`/api/adventures/${adventureId}/validation`)
    return response.data
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 404) return null
    throw err
  }
}

/**
 * Open validation counts for a page of adventures, one grouped query (at most 200 ids).
 * Ids the caller may not see are omitted from the result, as are those the API returns no
 * count for; read a missing id as zero.
 */
export async function getAdventureValidationCounts(adventureIds: string[]): Promise<Record<string, number>> {
  if (adventureIds.length === 0) return {}
  const response = await apiClient.post<Record<string, number>>('/api/adventures/validation-counts', {
    adventureIds: adventureIds.slice(0, 200),
  })
  return response.data ?? {}
}
